import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackButton from "../Navigation/BackButton";
import "./projects.style.css";
import "../Timesheets/timesheet-list.style.css";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import StyledModalView from "../Modal/StyledModalView";
import { isOwnerOrFinance } from "../../utils/role-check";


function ProjectTimeSheetListView({
    timesheets,
    allTimesheets,
    filterStatus,
    setFilterStatus,
    filterUser,
    setFilterUser,
    sortOrder,
    setSortOrder,
    loading,
    error,
    handleApprove,
    handleReject,
    confirmDelete,
    handleDeleteConfirmed,
    showDeleteModal,
    setShowDeleteModal,
    auth
}) {
    const { t } = useTranslation();
    const { id: projectId } = useParams();
    const [search, setSearch] = useState("");

    const badgeMap = {
        pending: t('timesheets.pending'),
        approved_by_voorman: t('timesheets.approvedByVoorman'),
        approved: t('timesheets.approved'),
        rejected: t('timesheets.rejected')
    };

    function getStatusBadge(status) {
        return <span className={`status-badge status-${status}`}>{badgeMap[status] || t('common.unknown')}</span>;
    }

    function getStatusText(status) {
        return badgeMap[status] || t('common.unknown');
    }

    function formatDate(dateString) {
        if (!dateString) return "-";
        const d = new Date(dateString);
        if (isNaN(d)) return dateString;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    }

    function getTotalHours(ts) {
        if (!ts.hours_worked) return 0;
        return Object.values(ts.hours_worked).reduce((sum, h) => sum + (parseFloat(h) || 0), 0);
    }

    // Apply search filter
    const filteredTimesheets = timesheets.filter(ts => {
        const q = search.toLowerCase();
        return (
            ts.user?.name?.toLowerCase().includes(q) ||
            ts.project?.name?.toLowerCase().includes(q) ||
            String(ts.week_number).includes(q) ||
            String(ts.year).includes(q)
        );
    });

    // Sort by date descending (newest first)
    const sortedTimesheets = [...filteredTimesheets].sort((a, b) => {
        const dateA = new Date(a.date || a.created_at);
        const dateB = new Date(b.date || b.created_at);
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    // Calculate totals for approved timesheets
    const approvedTimesheets = allTimesheets.filter(ts => ts.status === "approved");
    const totalApprovedHours = approvedTimesheets.reduce((sum, ts) => sum + getTotalHours(ts), 0);
    const totalPersonnelCosts = approvedTimesheets.reduce((sum, ts) => {
        const totalHours = getTotalHours(ts);
        let personnelCost = 0;

        if (ts.status === "approved" || ts.status === "approved_by_voorman") {
            if (ts.project_user && ts.project_user.rate_type === 'meters') {
                personnelCost = totalHours * (ts.meter_rate_at_approval || 0);
            } else if (ts.project_user && ts.project_user.rate_type === 'hourly') {
                personnelCost = totalHours * (ts.hourly_rate_at_approval || 0);
            } else {
                personnelCost = ts.personnel_costs || 0;
            }
        } else {
            if (ts.project_user && ts.project_user.rate_type === 'meters') {
                personnelCost = totalHours * (ts.project_user.meter_rate || 0);
            } else if (ts.project_user && ts.project_user.rate_type === 'hourly') {
                personnelCost = totalHours * (ts.project_user.hourly_rate || 0);
            } else {
                personnelCost = ts.personnel_costs || 0;
            }
        }

        // Add material costs
        const materialCosts = (ts.materials || []).reduce((matSum, material) =>
            matSum + (material.quantity * material.cost), 0);

        return sum + personnelCost + materialCosts;
    }, 0);

    // Status counts for all timesheets
    const statusCounts = allTimesheets.reduce((acc, ts) => {
        acc[ts.status] = (acc[ts.status] || 0) + 1;
        return acc;
    }, {});
    const statusOrder = ["approved", "approved_by_voorman", "pending", "rejected"];

    // Compute filter options from the full, unfiltered timesheet list
    const allUserOptions = Array.from(new Set(allTimesheets.map(ts => ts.user?.name).filter(Boolean)));

    // Filters component
    const Filters = (
        <div className="filters timesheetlist-toolbar">
            <input
                className="timesheetlist-search"
                type="text"
                placeholder={t('timesheets.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
            />
            <div className="filters-dropdowns">
                <div className="filter-group">
                    <label>{t('common.status')}:</label>
                    <select value={filterStatus || ""} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="">{t('timesheets.allStatuses')}</option>
                        <option value="pending">{t('timesheets.pending')}</option>
                        <option value="approved_by_voorman">{t('timesheets.approvedByVoorman')}</option>
                        <option value="approved">{t('timesheets.approved')}</option>
                        <option value="rejected">{t('timesheets.rejected')}</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>{t('timesheets.employee')}:</label>
                    <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
                        <option value="">{t('timesheets.allEmployees')}</option>
                        {allUserOptions.map(name => (
                            <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>{t('timesheets.sorting')}:</label>
                    <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                        <option value="desc">{t('timesheets.newestFirst')}</option>
                        <option value="asc">{t('timesheets.oldestFirst')}</option>
                    </select>
                </div>
            </div>
        </div>
    );

    if (loading) return <Loader message={t('common.loading')} />;
    if (error) return <NoDataConstruction text={t('common.error')} />;

    return (
        <div className="projects-container">

            <BackButton label={t('timesheets.backToProject')} />
            <h1>{t('timesheets.timesheetsOverview')}</h1>

            {/* Create new timesheet button - only for owner/finance */}
            {auth.role !== "klant" && (
                <div className="create-timesheet-wrapper">
                    <Link to={`/timesheets/new?project=${projectId}`} className="btn btn-add">
                        {t('timesheets.newTimesheet')}
                    </Link>
                </div>
            )}

            {Filters}

            {/* Status tags summary */}
            {allTimesheets.length > 0 && (
                <div className="project-timesheet-status-summary">
                    {statusOrder.map(status => (
                        statusCounts[status] ? (
                            <span key={status} className={`status-badge-dashboard status-${status}`}>
                                {badgeMap[status]} <span className="status-summary-count">({statusCounts[status]})</span>
                            </span>
                        ) : null
                    ))}
                </div>
            )}

            {/* Totals summary at the top */}
            {approvedTimesheets.length > 0 && (
                <div className="project-timesheet-summary-card">
                    <div className="summary-item">
                        <span className="summary-label">{t('timesheets.totalHours')}</span>
                        <span className="summary-value">{totalApprovedHours.toLocaleString(t('common.locale'), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
                    </div>
                    {isOwnerOrFinance(auth?.role) && (
                        <div className="summary-item">
                            <span className="summary-label">{t('timesheets.totalPersonnelCosts')}</span>
                            <span className="summary-value">€ {totalPersonnelCosts.toLocaleString(t('common.locale'), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                </div>
            )}

            {sortedTimesheets.length === 0 && (
                <NoDataConstruction text={t('timesheets.noTimesheetsFound')} />
            )}

            {sortedTimesheets.length > 0 && (
                <div className="timesheet-list-grid">
                    {sortedTimesheets.map(ts => {
                        const totalHours = getTotalHours(ts);
                        const personnelCost = ts.status === "approved" || ts.status === "approved_by_voorman"
                            ? (ts.project_user && ts.project_user.rate_type === "meters"
                                ? (ts.meter_rate_at_approval || 0) * totalHours
                                : (ts.hourly_rate_at_approval || 0) * totalHours)
                            : (ts.project_user && ts.project_user.rate_type === "meters"
                                ? (ts.project_user.meter_rate || 0) * totalHours
                                : (ts.project_user.hourly_rate || 0) * totalHours);

                        // Calculate extra material costs
                        const materialCosts = (ts.materials || []).reduce((sum, material) =>
                            sum + (material.quantity * material.cost), 0);

                        // Total cost = personnel cost + material costs
                        const totalCost = personnelCost + materialCosts;

                        return (
                            <div key={ts.id} className="timesheet-card">
                                <div className="timesheet-card-header">
                                    <div className="timesheet-card-title">{ts.user?.name || t('common.unknown')}</div>
                                    <div className={`badge status-${ts.status}`}>{getStatusBadge(ts.status)}</div>
                                </div>
                                <div className="timesheet-card-details">
                                    <div><b>{t('timesheets.year')}:</b> <span>{ts.year}</span></div>
                                    <div><b>{t('timesheets.week')}:</b> <span>{ts.week_number}</span></div>
                                    <div><b>{t('timesheets.date')}:</b> <span>{formatDate(ts.created_at)}</span></div>
                                    <div><b>{t('timesheets.hours')}:</b> <span className="highlight">{totalHours.toLocaleString(t('common.locale'), { minimumFractionDigits: 1 })} {t('common.hours')}</span></div>
                                    {auth?.role !== 'klant' && (
                                        <div><b>{t('timesheets.costs')}:</b> <span>€ {totalCost.toLocaleString(t('common.locale'), { minimumFractionDigits: 2 })}</span></div>
                                    )}
                                </div>
                                <div className="timesheet-card-actions">
                                    {auth?.role !== 'klant' && (
                                        <Link to={`/timesheets/${ts.id}`} className="btn btn-primary full-width">
                                            {t('common.view')}
                                        </Link>
                                    )}
                                    {ts.status === "rejected" && ts.user_id === auth?.userId && (
                                        <Link to={`/timesheets/${ts.id}/edit`} className="btn btn-edit full-width">{t('common.edit')}</Link>
                                    )}
                                    {isOwnerOrFinance(auth?.role) && ["pending", "approved_by_voorman"].includes(ts.status) && (
                                        <>
                                            <button type="button" className="btn btn-accept" onClick={() => handleApprove(ts.id)}>{t('timesheets.approve')}</button>
                                            <button type="button" className="btn btn-deny" onClick={() => handleReject(ts.id)}>{t('timesheets.reject')}</button>
                                        </>
                                    )}
                                    {isOwnerOrFinance(auth?.role) && (
                                        <button type="button" className="btn btn-deny full-width" onClick={() => confirmDelete(ts.id)}>{t('common.delete')}</button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <StyledModalView
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title={t('timesheets.confirmDelete')}
                confirmText={t('timesheets.confirmDeleteButton')}
                closeText={t('common.cancel')}
                onConfirm={handleDeleteConfirmed}
            >
                <p>{t('timesheets.confirmDeleteMessage')}</p>
            </StyledModalView>
        </div>
    );
}

ProjectTimeSheetListView.propTypes = {
    timesheets: PropTypes.array.isRequired,
    allTimesheets: PropTypes.array.isRequired,
    filterStatus: PropTypes.string,
    setFilterStatus: PropTypes.func.isRequired,
    filterUser: PropTypes.string,
    setFilterUser: PropTypes.func.isRequired,
    sortOrder: PropTypes.string,
    setSortOrder: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    handleApprove: PropTypes.func.isRequired,
    handleReject: PropTypes.func.isRequired,
    confirmDelete: PropTypes.func.isRequired,
    handleDeleteConfirmed: PropTypes.func.isRequired,
    showDeleteModal: PropTypes.bool.isRequired,
    setShowDeleteModal: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired,
};

export default ProjectTimeSheetListView;
